import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";
import { Template } from "@/content/templates/template.types";

import { TemplateGuideSection } from "../sections/template-guide-section";

import {
    TemplateBenefitsSection,
    tabbedSectionHeading
} from "./template-benefits-section.tsx";

const tabStyles = css({
    "backgroundColor": "#F0F1F2",
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "padding": "8px 24px 8px 24px",
    "borderRadius": "24px",
    "cursor": "pointer",
    "border": "2px solid #F0F1F2",
    "fontSize": "18px",
    ":hover": {
        border: "2px solid #0C66E4",
        backgroundColor: "#E9F2FE",
        color: "#0C66E4"
    }
});

const currentStyle = css({
    border: "2px solid #0C66E4",
    backgroundColor: "#E9F2FE",
    color: "#0C66E4"
});

const tabSectionStyle = css({
    marginBlockStart: "56px"
});

const TabHeading = ({
    title,
    isSelected
}: {
    title: string;
    isSelected?: boolean;
}) => {
    return (
        <div css={[tabStyles, isSelected && currentStyle]} className="h5">
            {title}
        </div>
    );
};

interface TemplateTabbedSectionProps {
    template: Template;
}

export const TemplateTabbedSection = ({
    template
}: TemplateTabbedSectionProps) => {
    const intl = useIntl();

    const tabs = [
        {
            id: "tab1",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.overview.cfind.1508",
                defaultMessage: "Overview"
            }),
            hide: !template.templateOverview,
            component: (
                <section css={tabSectionStyle}>
                    {template.templateOverview?.map((overview, index) => (
                        <section key={`overview-${index}`}>
                            <h2 css={css(tabbedSectionHeading)}>
                                {overview.overviewTitle}
                            </h2>
                            {overview.overviewDescription && (
                                <SanitizedRichText
                                    innerHTML={overview.overviewDescription}
                                />
                            )}
                        </section>
                    ))}
                </section>
            )
        },
        {
            id: "tab2",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.benefits.cfind.1508",
                defaultMessage: "Benefits"
            }),
            hide: !template.templateBenefits,
            component: (
                <section>
                    {template.templateBenefits &&
                        template.templateBenefits.map(
                            (benefitsSection, index) => (
                                <TemplateBenefitsSection
                                    key={`benefits-${index}`}
                                    {...benefitsSection}
                                />
                            )
                        )}
                </section>
            )
        },
        {
            id: "tab3",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.howTo.cfind.1508",
                defaultMessage: "How to"
            }),
            hide: !template.howToUseThisTemplate,
            component: (
                <section>
                    {template.howToUseThisTemplate &&
                        template.howToUseThisTemplate.map(
                            (howToSection, index) => (
                                <TemplateGuideSection
                                    key={`how2use-${index}`}
                                    templateGuide={howToSection}
                                />
                            )
                        )}
                </section>
            )
        },
        {
            id: "tab4",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.whyConfluence.cfind.1508",
                defaultMessage: "Why Confluence?"
            }),
            // temporary content for demoing... will be replaced with content from template object
            hide: !template.templateProductRationale,

            component: (
                <section css={tabSectionStyle}>
                    {template.templateProductRationale?.map(
                        (rationale, index) => (
                            <section key={`rationale-${index}`}>
                                <h2 css={tabbedSectionHeading}>
                                    {rationale.rationaleTitle}
                                </h2>
                                {rationale.templateProductRationaleDescription && (
                                    <SanitizedRichText
                                        innerHTML={
                                            rationale.templateProductRationaleDescription
                                        }
                                    />
                                )}
                            </section>
                        )
                    )}
                </section>
            )
        }
    ];

    const styles = css({
        "display": "flex",
        "@media(min-width: 768px)": {
            justifyContent: "center"
        }
    });

    return (
        <section
            css={css({
                "color": " #172B4D",
                "paddingBlockStart": "64px",
                "width": "100%",
                "display": "flex",
                "flexDirection": "column",
                "@media(max-width: 768px)": {
                    overflow: "scroll",
                    width: "100%"
                }
            })}
        >
            <Adg4Tabs>
                <Adg4DraggableTabList css={styles}>
                    {tabs
                        .filter((tab) => !tab.hide)
                        .map((tab) => (
                            <Adg4DraggableTab key={tab.id} title={tab.title}>
                                <TabHeading title={tab.title} />
                            </Adg4DraggableTab>
                        ))}
                </Adg4DraggableTabList>
                {tabs
                    .filter((tab) => !tab.hide)
                    .map((tab, index) => (
                        <Adg4TabPanel key={index} keepInDom={true}>
                            <>{tab.component}</>
                        </Adg4TabPanel>
                    ))}
            </Adg4Tabs>
        </section>
    );
};
